import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import TranslateTabBar from "../../components/common/TranslateTabBar";
import AddIcon from "../../assets/images/icon/add.png";
import UndoIcon from "../../assets/images/icon/undo.png";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toastError } from "../../components/Toast";
import TrainingService from "../../services/training-group";
import Loader from "../../assets/images/icon/LinkoSpinner.gif";
import ChatService from "../../services/chat";
import { BadgeCheckIcon, CircleX, Paperclip, AlertCircle, Shield } from "lucide-react";
import ReactMarkdown from "react-markdown";
import DOMPurify from "dompurify";
import { generatePdfFromHtml } from '../../utils/pdfFonts';
import { generateWordFromHtml } from '../../utils/wordExport';
import Logo from "../../assets/images/logo/logo-icon.jpg";
import UserProfile from "../../assets/images/icon/users.jpg";
import { useDispatch, useSelector } from "react-redux";
import { setInitial } from "../../store/slices/chatSlice";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { markdownToHtml, MD_PDF_STYLE } from "../../utils/markdownHtml";
import DropdownIcon from "../../assets/images/icon/dropdown.png";
import PurpleDropdownIcon from "../../assets/images/icon/purpledropdown.png";
import UploadFileIcon from "../../assets/images/icon/uploadfile.png";
import CopyIcon from "../../assets/images/icon/copy.png";
import ExportIcon from "../../assets/images/icon/export.png";
import WarningIcon from "../../assets/images/icon/warning.png";
import PauseIcon from "../../assets/images/icon/pause.png";
import countryData from "../../components/Languages.json";
import UploadService from "../../services/pdf-upload";
import HistoryService from "../../services/history";
import MenuAIPanel from "../../components/common/MenuAIPanel";
import ContactUsPopup, { AlreadySentPopup } from "../../components/common/ContactUsPopup";
import { readStoredName, readStoredImage, readStoredEmail, readStoredPlanName, readStoredGroupName } from "../../hooks/useSidebarData";
import AIIcon from "../../assets/images/icon/AI Icon.png";
import PaymentService from "../../services/payment";

const renderHighlightedString = (str) => {
  if (typeof str !== "string" || !str.includes("<mark")) return str;
  const cleanStr = str
    .replace(/(<mark[^>]*>)\s*(<mark[^>]*>)+/gi, "$1")
    .replace(/(<\/mark>)\s*(<\/mark>)+/gi, "$1");
  const parts = [];
  const regex = /<mark[^>]*>([\s\S]*?)<\/mark>/gi;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(cleanStr)) !== null) {
    if (match.index > lastIndex) {
      const plain = cleanStr.slice(lastIndex, match.index).replace(/<\/?mark[^>]*>/gi, "");
      if (plain) parts.push(plain);
    }
    const inner = match[1].replace(/<\/?mark[^>]*>/gi, "");
    parts.push(
      <mark
        key={`chat-mark-${key++}`}
        className="bg-[#D8CEF6] text-[#3D2B90] font-semibold inline px-1.5 py-0.5 rounded-[4px] border-0"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {inner}
      </mark>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < cleanStr.length) {
    const plain = cleanStr.slice(lastIndex).replace(/<\/?mark[^>]*>/gi, "");
    if (plain) parts.push(plain);
  }
  return parts;
};

// Fixed renderContent with proper ref handling
const renderContent = (content) => {
  if (!content) return null;

  const preprocessMarkdown = (text) => {
    if (!text) return "";
    let processed = String(text).replace(/\r\n/g, "\n");
    // Flatten nested/consecutive mark tags like <mark><mark>...</mark></mark>
    processed = processed
      .replace(/(<mark[^>]*>)\s*(<mark[^>]*>)+/gi, "$1")
      .replace(/(<\/mark>)\s*(<\/mark>)+/gi, "$1");
    // Collapse 3 or more consecutive newlines to at most 2 newlines
    processed = processed.replace(/\n{3,}/g, "\n\n");
    // Unindent lines with <mark> so markdown does not turn them into indented code blocks
    processed = processed.replace(/^\s{2,}(.*<mark>[\s\S]*?<\/mark>.*)/gm, "$1");
    // Unwrap backticks around <mark> tags
    processed = processed.replace(/`\s*(<mark>[\s\S]*?<\/mark>)\s*`/g, "$1");
    // Connect standalone punctuation on newlines after <mark>
    processed = processed.replace(/\n\s*(\.|\,|\?|\!)/g, "$1");
    processed = processed.replace(/^(\s*)[\*\+]\s+/gm, "$1- ");
    processed = processed.replace(/^(#{1,6})([^#\s\n])/gm, "$1 $2");

    // Only add a newline before/after headers, code blocks, lists if not already separated
    processed = processed.replace(/([^\n])\n(#{1,6}\s+)/g, "$1\n\n$2");
    processed = processed.replace(/(#{1,6}\s+[^\n]+)\n([^\n#])/g, "$1\n\n$2");
    processed = processed.replace(/([^\n])\n(```)/g, "$1\n\n$2");
    processed = processed.replace(/(```[^\n]*)\n([^\n`])/g, "$1\n\n$2");
    processed = processed.replace(/([^\s`])`([^`]+)`([^\s`])/g, "$1 `$2` $3");

    // Clean up excessive newlines again
    processed = processed.replace(/\n{3,}/g, "\n\n");

    const lines = processed.split("\n");
    let inList = false;
    let listLevel = 0;
    for (let i = 0; i < lines.length; i++) {
      const listMatch = lines[i].match(/^(\s*)([-*+]|\d+\.)\s/);
      if (listMatch) {
        const currentIndent = listMatch[1].length;
        if (!inList) {
          inList = true;
          listLevel = currentIndent;
        } else if (currentIndent > listLevel) {
          const properIndent = listLevel + 2;
          if (currentIndent < properIndent) {
            lines[i] = " ".repeat(properIndent) + lines[i].trim();
          }
        }
      } else if (lines[i].trim() === "") {
        inList = false;
      }
    }
    processed = lines.join("\n");
    return processed.replace(/\n{3,}/g, "\n\n");
  };

  const processedContent = preprocessMarkdown(content);

  const hasMarkdown =
    /(\*\*|__|~~|```|`|\[.*?\]\(.*?\)|^[-*+]\s|^#{1,6}\s|^\d+\.\s|<[a-z][a-z0-9]*(\s|>)|<\/[a-z][a-z0-9]*>)/im.test(
      processedContent
    );

  if (hasMarkdown) {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          p: ({ node, ...props }) => (
            <p
              className="last:mb-0 mb-2"
              style={{
                wordWrap: "break-word",
                overflowWrap: "break-word",
                maxWidth: "100%"
              }}
              {...props}
            />
          ),
          ul: ({ node, ...props }) => (
            <ul
              style={{
                marginBottom: "0.75rem",
                marginLeft: "1.25rem",
                listStyleType: "disc",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              style={{
                marginBottom: "0.75rem",
                marginLeft: "1.25rem",
                listStyleType: "decimal",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          li: ({ node, children, ...props }) => {
            const hasNestedList = React.Children.toArray(children).some(
              (child) =>
                React.isValidElement(child) &&
                (child.type === "ul" || child.type === "ol")
            );
            return (
              <li
                style={{
                  marginBottom: hasNestedList ? "0.2rem" : "0.35rem",
                  wordWrap: "break-word",
                  overflowWrap: "break-word"
                }}
                {...props}
              >
                {children}
              </li>
            );
          },
          h1: ({ node, ...props }) => (
            <h1
              style={{
                marginTop: "0.75rem",
                marginBottom: "0.4rem",
                fontSize: "1.5rem",
                fontWeight: "bold",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              style={{
                marginTop: "0.75rem",
                marginBottom: "0.4rem",
                fontSize: "1.25rem",
                fontWeight: "bold",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              style={{
                marginTop: "0.5rem",
                marginBottom: "0.3rem",
                fontSize: "1.1rem",
                fontWeight: "bold",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          h4: ({ node, ...props }) => (
            <h4
              style={{
                marginTop: "1rem",
                marginBottom: "0.5rem",
                fontSize: "1.25rem",
                fontWeight: "bold",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          h5: ({ node, ...props }) => (
            <h5
              style={{
                marginTop: "0.75rem",
                marginBottom: "0.5rem",
                fontSize: "1.1rem",
                fontWeight: "bold",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          h6: ({ node, ...props }) => (
            <h6
              style={{
                marginTop: "0.75rem",
                marginBottom: "0.5rem",
                fontSize: "1rem",
                fontWeight: "bold",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          a: ({ node, ...props }) => (
            <a
              style={{
                color: "#0366d6",
                textDecoration: "none",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              style={{
                borderLeft: "4px solid #dfe2e5",
                paddingLeft: "1rem",
                marginLeft: "0",
                marginRight: "0",
                marginTop: "1rem",
                marginBottom: "1rem",
                color: "#6a737d",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          code: ({ node, inline, className, children, ...props }) => {
            const rawText = typeof children === "string" ? children : Array.isArray(children) ? children.map(c => typeof c === "string" ? c : "").join("") : "";
            if (rawText && rawText.includes("<mark>")) {
              return <span className="inline">{renderHighlightedString(rawText)}</span>;
            }
            return inline ? (
              <code
                style={{
                  background: "#f1f1f1",
                  padding: "0.2em 0.4em",
                  borderRadius: "3px",
                  fontFamily: "monospace",
                  fontSize: "0.85em",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap"
                }}
                {...props}
              >
                {children}
              </code>
            ) : (
              <code
                className={className}
                style={{
                  display: "block",
                  background: "#f6f8fa",
                  padding: "1rem",
                  borderRadius: "5px",
                  fontFamily: "monospace",
                  fontSize: "0.85em",
                  overflowX: "auto",
                  marginTop: "1rem",
                  marginBottom: "1rem",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap"
                }}
                {...props}
              >
                {children}
              </code>
            );
          },
          pre: ({ node, children, ...props }) => {
            const rawText = node?.children?.[0]?.children?.[0]?.value || "";
            if (rawText && rawText.includes("<mark>")) {
              return <span className="inline">{renderHighlightedString(rawText)}</span>;
            }
            return (
              <pre
                style={{
                  background: "transparent",
                  padding: "0",
                  margin: "0",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap"
                }}
                {...props}
              >
                {children}
              </pre>
            );
          },
          table: ({ node, ...props }) => (
            <div style={{
              overflowX: "auto",
              marginTop: "1rem",
              marginBottom: "1rem",
              maxWidth: "100%"
            }}>
              <table
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  border: "1px solid #dfe2e5",
                  wordWrap: "break-word",
                  overflowWrap: "break-word"
                }}
                {...props}
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead style={{ background: "#f6f8fa" }} {...props} />
          ),
          th: ({ node, ...props }) => (
            <th
              style={{
                padding: "0.5rem 1rem",
                border: "1px solid #dfe2e5",
                fontWeight: "600",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              style={{
                padding: "0.5rem 1rem",
                border: "1px solid #dfe2e5",
                wordWrap: "break-word",
                overflowWrap: "break-word"
              }}
              {...props}
            />
          ),
          hr: ({ node, ...props }) => (
            <hr
              style={{
                height: "0.25em",
                padding: "0",
                margin: "1.5rem 0",
                background: "#e1e4e8",
                border: "0"
              }}
              {...props}
            />
          ),
          mark: ({ node, children, ...props }) => (
            <mark
              className="bg-[#D8CEF6] text-[#3D2B90] font-semibold inline px-1.5 py-0.5 rounded-[4px] border-0 [&_mark]:p-0 [&_mark]:border-0 [&_mark]:bg-transparent"
              style={{ fontFamily: 'Inter, sans-serif' }}
              {...props}
            >
              {children}
            </mark>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    );
  }

  const codeBlockMatch = processedContent.match(/^```html\n([\s\S]*?)\n```$/);
  if (codeBlockMatch) {
    const htmlContent = codeBlockMatch[1].trim();
    const sanitizedHtml = DOMPurify.sanitize(htmlContent);
    return <div
      style={{
        wordWrap: "break-word",
        overflowWrap: "break-word",
        maxWidth: "100%"
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />;
  }

  if (processedContent.includes("<") && processedContent.includes(">")) {
    const sanitizedHtml = DOMPurify.sanitize(processedContent);
    return <div
      style={{
        wordWrap: "break-word",
        overflowWrap: "break-word",
        maxWidth: "100%"
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />;
  }

  return (
    <p
      className="m-0"
      style={{
        margin: 0,
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        maxWidth: "100%"
      }}
    >
      {processedContent}
    </p>
  );
};

const getTimestamp = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
};

const getBase64Image = async (url) => {
  if (!url) return null;
  if (url.startsWith("data:")) return url;
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(url);
      reader.readAsDataURL(blob);
    });
  } catch (_e) {
    return url;
  }
};

const isImageAttachment = (url, fileName) => {
  if (!url && !fileName) return false;
  if (typeof url === "string" && (url.startsWith("data:image/") || url.startsWith("blob:"))) {
    if (fileName) {
      const ext = fileName.toLowerCase().split(".").pop();
      if (["docx", "doc", "pdf", "txt", "ppt", "pptx", "xls", "xlsx", "csv"].includes(ext)) {
        return false;
      }
    }
    return true;
  }
  const target = (fileName || url || "").toLowerCase().split("?")[0].split("#")[0];
  return /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i.test(target);
};

const handleOpenFile = (fileUrl, fileName, fileBlob) => {
  if (fileBlob instanceof Blob) {
    const url = URL.createObjectURL(fileBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "document";
    if (fileName && fileName.toLowerCase().endsWith(".pdf")) {
      window.open(url, "_blank");
    } else {
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    }
    return;
  }

  if (!fileUrl) return;

  const isPdf = /\.pdf$/i.test(fileName || fileUrl);
  if (isPdf) {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
    return;
  }

  const a = document.createElement("a");
  a.href = fileUrl;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.download = fileName || (fileUrl ? fileUrl.split("/").pop() : "document");
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
  }, 100);
};

const LS_CHAT_KEY = "linko_ai_chat_messages";

// Module-level active stream manager to keep streaming updates connected across tab unmount/remount
const activeStreamManager = {
  isStreaming: false,
  streamPi: null,
  currentLiveText: "",
  listeners: new Set(),
  abortController: null,

  subscribe(listener) {
    this.listeners.add(listener);
    if (this.isStreaming || this.currentLiveText) {
      listener({
        type: "sync",
        isStreaming: this.isStreaming,
        liveText: this.currentLiveText,
        streamPi: this.streamPi,
      });
    }
    return () => {
      this.listeners.delete(listener);
    };
  },

  notify(event) {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (_) { }
    });
  },

  start(abortController) {
    this.isStreaming = true;
    this.streamPi = null;
    this.currentLiveText = "";
    this.abortController = abortController;
    this.notify({ type: "start", isStreaming: true });
  },

  updateText(text, pi) {
    this.currentLiveText = text;
    if (typeof pi === "number") this.streamPi = pi;
    this.notify({
      type: "update",
      liveText: text,
      streamPi: this.streamPi,
      isStreaming: true,
    });
  },

  updatePi(pi) {
    if (typeof pi === "number") this.streamPi = pi;
    this.notify({
      type: "pi",
      streamPi: this.streamPi,
      isStreaming: true,
    });
  },

  finish(finalText, finalPi) {
    this.isStreaming = false;
    if (finalText) this.currentLiveText = finalText;
    if (typeof finalPi === "number") this.streamPi = finalPi;
    this.notify({
      type: "finish",
      liveText: this.currentLiveText,
      streamPi: this.streamPi,
      isStreaming: false,
    });
  },

  abort() {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.isStreaming = false;
    this.notify({ type: "abort", isStreaming: false });
  },
};

const getSavedChatMessages = () => {
  try {
    const saved = localStorage.getItem(LS_CHAT_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (_) { }
  return [];
};

const Chat = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [username, setUsername] = useState(
    () => readStoredName() || "Usuario"
  );
  const [imagePreview, setImagePreview] = useState(readStoredImage);

  useEffect(() => {
    const syncChatUser = () => {
      setUsername(readStoredName() || "Usuario");
      setImagePreview(readStoredImage());
    };
    syncChatUser();
    window.addEventListener("profileUpdated", syncChatUser);
    return () => window.removeEventListener("profileUpdated", syncChatUser);
  }, []);

  const user = useSelector((state) => state.user);
  const chat = useSelector((state) => state.chat);
  const dispatch = useDispatch();
  const abortControllerRef = useRef(null);
  const dropdownRef = useRef(null);
  const textareaRef = useRef(null);
  const messageEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const fileUploadRef = useRef(null);

  const scrollTrackRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const thumbTopRef = useRef(0);
  const thumbHRef = useRef(52);
  const THUMB_MIN_H = 52;

  const updateScrollThumb = useCallback(() => {
    const el = chatScrollRef.current;
    const track = scrollTrackRef.current;
    const thumb = scrollThumbRef.current;
    if (!el || !track || !thumb) return;
    const trackH = track.clientHeight;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflow = scrollHeight > clientHeight + 1;
    track.style.visibility = overflow ? "visible" : "hidden";
    if (!overflow) return;
    const ratio = clientHeight / scrollHeight;
    const h = Math.max(THUMB_MIN_H, ratio * trackH);
    const maxThumbTop = trackH - h;
    const top = scrollHeight === clientHeight ? 0 : (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop;
    thumbHRef.current = h;
    thumbTopRef.current = top;
    thumb.style.top = top + "px";
    thumb.style.height = h + "px";
  }, []);

  const [initialRender, setInitialRender] = useState(() => getSavedChatMessages().length === 0);
  const [loading, setLoading] = useState(false);
  const [showNewChatConfirm, setShowNewChatConfirm] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState("");
  const [suggestion, setsuggestion] = useState("");
  const [messages, setMessages] = useState(getSavedChatMessages);
  const [isLoading, setIsLoading] = useState(() => activeStreamManager.isStreaming);
  const [showExportPicker, setShowExportPicker] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState("");
  const [complete, setComplete] = useState(false);
  const [personalIdentifiersProtected, setPersonalIdentifiersProtected] = useState(() => activeStreamManager.streamPi);
  const [privacyStatus, setPrivacyStatus] = useState(() => activeStreamManager.isStreaming ? "protecting" : "default");
  const privacyTimerRef = useRef(null);

  useEffect(() => {
    const unsubscribe = activeStreamManager.subscribe((event) => {
      if (event.type === "start" || event.isStreaming) {
        setIsLoading(true);
        setPrivacyStatus("protecting");
      }
      if (event.liveText) {
        setMessages((prev) => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].type === "bot") {
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: event.liveText,
              personalIdentifiersProtected: typeof event.streamPi === "number" ? event.streamPi : updated[updated.length - 1].personalIdentifiersProtected,
            };
          }
          return updated;
        });
        setInitialRender(false);
        dispatch(setInitial(false));
      }
      if (typeof event.streamPi === "number") {
        setPersonalIdentifiersProtected(event.streamPi);
      }
      if (event.type === "finish") {
        setIsLoading(false);
        setPrivacyStatus("completed");
        if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
        privacyTimerRef.current = setTimeout(() => {
          setPrivacyStatus("reminder");
        }, 5000);
      } else if (event.type === "abort") {
        setIsLoading(false);
        setPrivacyStatus("default");
      }
    });
    return () => unsubscribe();
  }, [dispatch]);

  useEffect(() => {
    if (messages && messages.length > 0) {
      try {
        localStorage.setItem(LS_CHAT_KEY, JSON.stringify(messages));
      } catch (_) { }
    }
  }, [messages]);

  useEffect(() => {
    return () => {
      if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
    };
  }, []);

  // File upload state for chat textfield
  const [chatFile, setChatFile] = useState(null);
  const [chatFilePreview, setChatFilePreview] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const chatFileInputRef = useRef(null);

  const handleChatFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setChatFile(file);
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => setChatFilePreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setChatFilePreview(null);
      }
    }
    e.target.value = null;
  };

  const handleRemoveChatFile = () => {
    setChatFile(null);
    setChatFilePreview(null);
    if (chatFileInputRef.current) {
      chatFileInputRef.current.value = "";
    }
  };
  const [isClick, setIsClick] = useState(false);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const [autoScrollPaused, setAutoScrollPaused] = useState(false);

  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [showAiTooltip, setShowAiTooltip] = useState(false);
  const [presets, setPresets] = useState([]);

  const [chatHistory, setChatHistory] = useState([]);
  const reduxUser = useSelector((state) => state.user?.user);
  const reduxTokenBalance = useSelector((state) => state.user?.tokenBalance);
  const reduxTokenLimit = useSelector((state) => state.user?.tokenLimit);

  const [availableTokens, setAvailableTokens] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("linko_token_status") || "null");
      return typeof cached?.availableTokens === "number" ? cached.availableTokens : null;
    } catch (_) { return null; }
  });
  const [tokenTotal, setTokenTotal] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("linko_token_status") || "null");
      return typeof cached?.tokenTotal === "number" ? cached.tokenTotal : null;
    } catch (_) { return null; }
  });

  useEffect(() => {
    if (reduxTokenBalance !== undefined && reduxTokenBalance !== null) {
      setAvailableTokens(reduxTokenBalance);
    }
  }, [reduxTokenBalance]);

  useEffect(() => {
    if (reduxTokenLimit !== undefined && reduxTokenLimit !== null) {
      setTokenTotal(reduxTokenLimit);
    }
  }, [reduxTokenLimit]);

  const checkAiAccess = useCallback(() => {
    let cachedUser = null;
    try {
      cachedUser = JSON.parse(localStorage.getItem("linko_user_profile") || "null");
    } catch (_) {}
    const isMissingVal = (v) => !v || v === "undefined" || v === "null";
    const rawPlan = readStoredPlanName();
    const validPlan = !isMissingVal(rawPlan) ? rawPlan : "";

    const storedPlan = (
      validPlan ||
      reduxUser?.plan_name ||
      cachedUser?.plan_name ||
      reduxUser?.trainingGroupId?.plan_name ||
      cachedUser?.trainingGroupId?.plan_name ||
      ""
    ).toLowerCase();

    const rawGroup = readStoredGroupName();
    const validGroup = !isMissingVal(rawGroup) ? rawGroup : "";

    const storedGroup = (
      validGroup ||
      reduxUser?.trainingGroupId?.groupName ||
      cachedUser?.trainingGroupId?.groupName ||
      ""
    ).toLowerCase();

    return (
      storedPlan.includes("adminprovide") ||
      (!isMissingVal(storedPlan) && (storedPlan.includes("org") || storedPlan.includes("organiza") || storedPlan.includes("contact"))) ||
      (!isMissingVal(storedGroup) && Boolean(storedGroup)) ||
      Boolean(reduxUser?.trainingGroupId) ||
      Boolean(cachedUser?.trainingGroupId)
    );
  }, [reduxUser]);

  const [hasAiAccess, setHasAiAccess] = useState(() => {
    try {
      let cachedUser = null;
      try {
        cachedUser = JSON.parse(localStorage.getItem("linko_user_profile") || "null");
      } catch (_) {}
      const isMissingVal = (v) => !v || v === "undefined" || v === "null";
      const rawPlan = readStoredPlanName();
      const validPlan = !isMissingVal(rawPlan) ? rawPlan : "";
      const storedPlan = (
        validPlan ||
        cachedUser?.plan_name ||
        cachedUser?.trainingGroupId?.plan_name ||
        ""
      ).toLowerCase();
      const rawGroup = readStoredGroupName();
      const validGroup = !isMissingVal(rawGroup) ? rawGroup : "";
      const storedGroup = (
        validGroup ||
        cachedUser?.trainingGroupId?.groupName ||
        ""
      ).toLowerCase();
      return (
        storedPlan.includes("adminprovide") ||
        (!isMissingVal(storedPlan) && (storedPlan.includes("org") || storedPlan.includes("organiza") || storedPlan.includes("contact"))) ||
        (!isMissingVal(storedGroup) && Boolean(storedGroup)) ||
        Boolean(cachedUser?.trainingGroupId)
      );
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    setHasAiAccess(checkAiAccess());
  }, [checkAiAccess]);

  useEffect(() => {
    const handleUpdate = () => {
      setHasAiAccess(checkAiAccess());
    };
    window.addEventListener("profileUpdated", handleUpdate);
    window.addEventListener("planUpdated", handleUpdate);
    window.addEventListener("authChanged", handleUpdate);
    return () => {
      window.removeEventListener("profileUpdated", handleUpdate);
      window.removeEventListener("planUpdated", handleUpdate);
      window.removeEventListener("authChanged", handleUpdate);
    };
  }, [checkAiAccess]);

  const [showContactUs, setShowContactUs] = useState(false);
  const [isContactUsSubmitting, setIsContactUsSubmitting] = useState(false);
  const [contactUsSuccess, setContactUsSuccess] = useState(false);
  const [showAlreadySentPopup, setShowAlreadySentPopup] = useState(false);

  const isContactRequestSent = useCallback(() => {
    if (hasAiAccess) return false;
    const note = localStorage.getItem("linko_rejection_note");
    if (note) return false;
    return (
      localStorage.getItem("linko_contact_sent") === "true" ||
      reduxUser?.is_contactus === true ||
      reduxUser?.is_contact_request === true ||
      reduxUser?.contact_request === true ||
      reduxUser?.is_request === true
    );
  }, [hasAiAccess, reduxUser]);

  const handleContactUsSubmit = async (form) => {
    try {
      setIsContactUsSubmitting(true);
      const payload = {
        email: form.email,
        name: form.name,
        phone_no: form.phone_no,
        country_code: form.country_code,
        note: form.note,
      };
      await PaymentService.contactUsPlan(payload);
      setShowContactUs(false);
      setContactUsSuccess(true);
      try {
        localStorage.setItem("linko_contact_sent", "true");
        localStorage.removeItem("linko_rejection_note");
      } catch (_) {}
      window.dispatchEvent(new CustomEvent("contactStatusChanged", { detail: { sent: true } }));
      window.dispatchEvent(new Event("planUpdated"));
      window.dispatchEvent(new Event("profileUpdated"));
    } catch (error) {
      toastError(
        error?.response?.data?.message ||
        "Failed to send request. Please try again."
      );
    } finally {
      setIsContactUsSubmitting(false);
    }
  };

  const isTokenExhausted =
    availableTokens === 0 ||
    (tokenTotal > 0 && availableTokens !== null && availableTokens <= 0);

  const isLimitReached =
    (tokenTotal > 0 &&
      availableTokens !== null &&
      ((tokenTotal - availableTokens) / tokenTotal) * 100 >= 90) ||
    availableTokens === 0;

  const isFetchingHistoryRef = useRef(false);

  const fetchChatHistory = async () => {
    if (isFetchingHistoryRef.current) return;
    try {
      isFetchingHistoryRef.current = true;
      const res = await HistoryService.getAIConversations();

      const rawData = res?.data?.data;
      const arr = Array.isArray(rawData)
        ? rawData
        : (Array.isArray(rawData?.conversations)
          ? rawData.conversations
          : (Array.isArray(res?.data?.conversations)
            ? res.data.conversations
            : (Array.isArray(res?.data?.aiConversations)
              ? res.data.aiConversations
              : (Array.isArray(res?.data)
                ? res.data
                : []))));
      setChatHistory(arr);
      // Auto-load all conversations as continuous chat messages
      if (arr.length > 0) {
        const formatted = [];
        // API returns newest first, reverse to show oldest first
        [...arr].reverse().forEach((item) => {
          if (Array.isArray(item.messages) && item.messages.length > 0) {
            item.messages.forEach((m) => {
              const isUser = m.role === "user" || m.type === "user";
              const mediaUrl = m.media || m.fileUrl || m.imageUrl || m.image || null;
              if (isUser) {
                formatted.push({
                  type: "user",
                  content: m.content || m.question || m.text || "",
                  media: mediaUrl,
                  filePreview: mediaUrl,
                  fileName: m.fileName || (mediaUrl ? mediaUrl.split("/").pop() : null),
                });
              } else {
                formatted.push({
                  type: "bot",
                  content: m.content || m.answer || m.text || m.translation || m.response || "",
                  personalIdentifiersProtected: typeof m.personalIdentifiersProtected === "number" ? m.personalIdentifiersProtected : null,
                });
              }
            });
          } else {
            const mediaUrl = item.media || item.fileUrl || item.imageUrl || item.image || null;
            const questionText = item.question || item.inputText || item.prompt || item.text || "";
            const answerText = item.answer || item.response || item.translation || item.content || "";

            if (questionText || mediaUrl) {
              formatted.push({
                type: "user",
                content: questionText,
                media: mediaUrl,
                filePreview: mediaUrl,
                fileName: item.fileName || (mediaUrl ? mediaUrl.split("/").pop() : null),
              });
            }
            if (answerText) {
              formatted.push({
                type: "bot",
                content: answerText,
                personalIdentifiersProtected: typeof item.personalIdentifiersProtected === "number" ? item.personalIdentifiersProtected : null,
              });
            }
          }
        });
        if (formatted.length > 0) {
          setMessages(formatted);
          try {
            localStorage.setItem(LS_CHAT_KEY, JSON.stringify(formatted));
          } catch (_) { }
          setInitialRender(false);
          dispatch(setInitial(false));
        }
      }
    } catch (err) {
      console.error("Failed to load AI conversations history", err);
    } finally {
      isFetchingHistoryRef.current = false;
    }
  };

  useEffect(() => {
    fetchChatHistory();
  }, []);

  const handleSelectHistoryItem = (item) => {
    const formatted = [];
    const mediaUrl = item.media || item.fileUrl || item.imageUrl || item.image || null;
    const questionText = item.question || item.inputText || item.prompt || "";
    const answerText = item.answer || item.response || item.translation || item.text || item.content || "";

    if (questionText || mediaUrl) {
      formatted.push({
        type: "user",
        content: questionText,
        media: mediaUrl,
        filePreview: mediaUrl,
        fileName: item.fileName || (mediaUrl ? mediaUrl.split("/").pop() : null),
      });
    }
    if (answerText) {
      formatted.push({
        type: "bot",
        content: answerText,
        personalIdentifiersProtected: typeof item.personalIdentifiersProtected === "number" ? item.personalIdentifiersProtected : null,
      });
    }
    // Also support legacy messages array format
    if (formatted.length === 0 && Array.isArray(item.messages) && item.messages.length > 0) {
      item.messages.forEach((m) => {
        formatted.push({
          type: m.role === "user" || m.type === "user" ? "user" : "bot",
          content: m.content || "",
          media: m.media || m.fileUrl || m.imageUrl || null,
          filePreview: m.media || m.fileUrl || m.imageUrl || null,
          fileName: m.fileName || null,
          personalIdentifiersProtected: typeof m.personalIdentifiersProtected === "number" ? m.personalIdentifiersProtected : null,
        });
      });
    }
    if (formatted.length > 0) {
      setMessages(formatted);
      try {
        localStorage.setItem(LS_CHAT_KEY, JSON.stringify(formatted));
      } catch (_) { }
      dispatch(setInitial(false));
      setInitialRender(false);
      setTimeout(() => {
        if (chatScrollRef.current) {
          chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
      }, 50);
    }
  };

  const langDropdownRef = useRef(null);

  // Audio Language state (for the toolbar)
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [langSearch, setLangSearch] = useState("");
  const [selectedAudioLang, setSelectedAudioLang] = useState("");

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLang, setUploadLang] = useState("");
  const [uploadLangSearch, setUploadLangSearch] = useState("");
  const [uploadLangOpen, setUploadLangOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadIntervalId, setUploadIntervalId] = useState(null);
  const uploadLangRef = useRef(null);
  const uploadFileInputRef = useRef(null);

  const filteredUploadLangs = countryData.filter((item) =>
    item.name.toLowerCase().includes(uploadLangSearch.toLowerCase())
  );

  const handleUploadFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) setUploadFile(file);
    e.target.value = null;
  };

  const handleUploadSubmit = async () => {
    if (!uploadLang) { toastError(t.fieldRequired); return; }
    if (!uploadFile) { toastError(t.fieldRequired); return; }
    setUploadLoading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);
    try {
      const response = await UploadService.uploadFile(formData, uploadLang);
      if (response?.data?.response?.task_id) {
        const id = response.data.response.task_id;
        if (uploadIntervalId) clearInterval(uploadIntervalId);
        const newIntervalId = setInterval(async () => {
          try {
            const status = await UploadService.status({ id });
            if (status?.data?.status === "completed") {
              clearInterval(newIntervalId);
              setUploadIntervalId(null);
              const result = await UploadService.result({ id: status.data.task_id });
              if (result?.data?.fileUrl) {
                const fetchRes = await fetch(result.data.fileUrl);
                const blob = await fetchRes.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = uploadFile.name
                  ? uploadFile.name.replace(/\.[^/.]+$/, "") + ".docx"
                  : "translated.docx";
                document.body.appendChild(a);
                a.click();
                setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 100);
              } else {
                toastError(t.downloadUrlError);
              }
              setUploadLoading(false);
              setShowUploadModal(false);
              setUploadFile(null);
              setUploadLang("");
            } else if (["failed", "error", "cancelled"].includes(status?.data?.status)) {
              clearInterval(newIntervalId);
              setUploadIntervalId(null);
              toastError(t.somethingWentWrong);
              setUploadLoading(false);
            }
          } catch {
            clearInterval(newIntervalId);
            setUploadIntervalId(null);
            toastError(t.somethingWentWrong);
            setUploadLoading(false);
          }
        }, 2000);
        setUploadIntervalId(newIntervalId);
      }
    } catch (error) {
      toastError(error?.response?.data?.message || t.somethingWentWrong);
      setUploadLoading(false);
    }
  };

  const handleCloseUploadModal = () => {
    if (uploadLoading) return;
    if (uploadIntervalId) clearInterval(uploadIntervalId);
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadLang("");
    setUploadLangSearch("");
    setUploadLangOpen(false);
  };

  const filteredLanguages = countryData.filter((item) =>
    item.name.toLowerCase().includes(langSearch.toLowerCase())
  );

  const toggleDropdown = () => {
    if (options?.length === 0) {
      setIsOpen(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleCopyClick = (text, index) => {
    if (text === undefined || text === null) return;

    // Remove internal PII <mark> highlighting tags while keeping all HTML tags, code, and text intact
    let cleanedText = String(text).replace(/<\/?mark[^>]*>/gi, "").trim();

    // If it's a code block (like ```html ... ```), extract the content cleanly
    const codeBlockMatch = cleanedText.match(/^```(?:html|[a-zA-Z0-9_-]+)?\r?\n([\s\S]*?)\r?\n```$/);
    if (codeBlockMatch) {
      cleanedText = codeBlockMatch[1].trim();
    }

    const doSuccess = () => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(""), 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(cleanedText).then(doSuccess).catch(() => {
        const ta = document.createElement("textarea");
        ta.value = cleanedText;
        ta.setAttribute("readonly", "");
        ta.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try {
          const ok = document.execCommand("copy");
          if (ok) doSuccess();
        } catch (_) { }
        document.body.removeChild(ta);
      });
    } else {
      const ta = document.createElement("textarea");
      ta.value = cleanedText;
      ta.setAttribute("readonly", "");
      ta.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        const ok = document.execCommand("copy");
        if (ok) doSuccess();
      } catch (_) { }
      document.body.removeChild(ta);
    }
  };

  const handleOptionClick = (option) => {
    setSelectedOptions(option);
    setIsOpen(false);
    setsuggestion(option);
  };

  const handleRemoveOption = () => {
    setsuggestion("");
    setSelectedOptions("");
  };

  // Scroll to bottom once history messages are loaded on mount
  useEffect(() => {
    if (messages.length > 0 && chatScrollRef.current) {
      setTimeout(() => {
        if (chatScrollRef.current) {
          chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
        updateScrollThumb();
      }, 100);
      setTimeout(() => updateScrollThumb(), 300);
      setTimeout(() => updateScrollThumb(), 700);
    }
  }, [messages.length]);

  // Scroll to bottom on every mount (tab switch navigates here fresh)
  useLayoutEffect(() => {
    const scrollToBottom = () => {
      if (chatScrollRef.current) {
        chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        updateScrollThumb();
      }
    };
    scrollToBottom();
    const t1 = setTimeout(scrollToBottom, 100);
    const t2 = setTimeout(scrollToBottom, 350);
    const t3 = setTimeout(scrollToBottom, 600);
    const t4 = setTimeout(scrollToBottom, 1000);
    const t5 = setTimeout(scrollToBottom, 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values) => {
    setIsClick(false);
    setIsUserScrolledUp(false);
    setAutoScrollPaused(false);
    if (isLoading || complete) return;
    const textInput = (values?.input || "").trim();
    if (!textInput && !chatFile) return;

    dispatch(setInitial(false));
    setInitialRender(false);
    setIsLoading(true);

    const currentFile = chatFile;
    const currentFilePreview = chatFilePreview;

    setChatFile(null);
    setChatFilePreview(null);
    formik.resetForm();

    const userMsg = {
      type: "user",
      content: textInput,
      filePreview: currentFilePreview,
      media: currentFilePreview,
      fileName: currentFile ? currentFile.name : null,
      fileBlob: currentFile,
    };

    // Placeholder bot message that will stream in progressively
    const initialBotMsg = { type: "bot", content: "", personalIdentifiersProtected: null };
    const initialUpdated = [...messages, userMsg, initialBotMsg];
    setMessages(initialUpdated);
    try {
      localStorage.setItem(LS_CHAT_KEY, JSON.stringify(initialUpdated));
    } catch (_) { }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    activeStreamManager.start(abortController);

    const formData = new FormData();
    if (textInput) formData.append("inputText", textInput);
    if (suggestion) formData.append("suggestion", suggestion);
    if (currentFile) formData.append("file", currentFile);

    if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
    setPrivacyStatus("protecting");
    let streamPi = null;

    try {
      const streamRes = await ChatService.createChatStream(
        formData,
        (event) => {
          if (typeof event?.personalIdentifiersProtected === "number") {
            streamPi = event.personalIdentifiersProtected;
            activeStreamManager.updatePi(streamPi);
          }
          if (event.type === "pii-complete" && typeof event?.personalIdentifiersProtected === "number") {
            streamPi = event.personalIdentifiersProtected;
            activeStreamManager.updatePi(streamPi);
          }
          const liveText = event.accumulated || event.translation || event.text || "";
          if (liveText) {
            activeStreamManager.updateText(liveText, streamPi);
            try {
              const saved = JSON.parse(localStorage.getItem(LS_CHAT_KEY) || "[]");
              if (saved.length > 0 && saved[saved.length - 1].type === "bot") {
                saved[saved.length - 1].content = liveText;
                if (typeof streamPi === "number") {
                  saved[saved.length - 1].personalIdentifiersProtected = streamPi;
                }
                localStorage.setItem(LS_CHAT_KEY, JSON.stringify(saved));
              }
            } catch (_) { }
          }
        },
        { signal }
      );
      const finalPi = typeof streamRes?.personalIdentifiersProtected === "number" ? streamRes.personalIdentifiersProtected : streamPi;
      const finalTranslation = streamRes?.translation || activeStreamManager.currentLiveText;
      activeStreamManager.finish(finalTranslation, finalPi);

      try {
        const saved = JSON.parse(localStorage.getItem(LS_CHAT_KEY) || "[]");
        if (saved.length > 0 && saved[saved.length - 1].type === "bot") {
          if (finalTranslation) saved[saved.length - 1].content = finalTranslation;
          if (typeof finalPi === "number") saved[saved.length - 1].personalIdentifiersProtected = finalPi;
          localStorage.setItem(LS_CHAT_KEY, JSON.stringify(saved));
        }
      } catch (_) { }
    } catch (error) {
      if (error?.name === "AbortError" || error?.message === "canceled" || signal?.aborted) {
        activeStreamManager.abort();
        return;
      }
      activeStreamManager.abort();
      toastError(error?.message || error?.response?.data?.message || t.somethingWentWrong);
      setMessages((prev) => {
        if (prev.length > 0 && prev[prev.length - 1].type === "bot" && !prev[prev.length - 1].content) {
          const updated = prev.slice(0, -1);
          try {
            localStorage.setItem(LS_CHAT_KEY, JSON.stringify(updated));
          } catch (_) { }
          return updated;
        }
        return prev;
      });
    } finally {
      setIsLoading(false);
      HistoryService.getAIConversations()
        .then((res) => {
          const rawData = res?.data?.data;
          const items = Array.isArray(rawData)
            ? rawData
            : (Array.isArray(rawData?.conversations)
              ? rawData.conversations
              : (Array.isArray(res?.data?.conversations)
                ? res.data.conversations
                : (Array.isArray(res?.data?.aiConversations)
                  ? res.data.aiConversations
                  : (Array.isArray(res?.data)
                    ? res.data
                    : []))));
          setChatHistory(items);
        })
        .catch(() => { });
    }
  };

  const formik = useFormik({
    initialValues: { input: "" },
    validationSchema: Yup.object({ input: Yup.string() }),
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!isLoading && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [formik.values.input]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setIsLangOpen(false);
      }
    };
    if (isLangOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isLangOpen]);

  const handleNewChatClick = async () => {
    setIsLoading(true);
    try {
      await ChatService.deleteAiConversations();

      // Only clear client state after backend successfully confirms deletion
      formik.setFieldValue("input", "");
      setChatFile(null);
      setChatFilePreview(null);
      if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
      setPrivacyStatus("default");
      setPersonalIdentifiersProtected(null);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      activeStreamManager.abort();
      try {
        localStorage.removeItem(LS_CHAT_KEY);
      } catch (_) { }
      dispatch(setInitial(true));
      setInitialRender(true);
      setMessages([]);
      setComplete(false);
      setIsUserScrolledUp(false);
      setAutoScrollPaused(false);
      setsuggestion("");
      setSelectedOptions("");

      // Refresh history list but don't auto-load into messages after new chat
      try {
        const res = await HistoryService.getAIConversations();
        const rawData = res?.data?.data;
        const items = Array.isArray(rawData)
          ? rawData
          : (Array.isArray(rawData?.conversations)
            ? rawData.conversations
            : (Array.isArray(res?.data?.conversations)
              ? res.data.conversations
              : (Array.isArray(res?.data?.aiConversations)
                ? res.data.aiConversations
                : (Array.isArray(res?.data)
                  ? res.data
                  : []))));
        setChatHistory(items);
      } catch (_) { }
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || t.somethingWentWrong || "Failed to start new chat. Please check your connection.";
      toastError(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const container = chatScrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom > 20) {
        setIsUserScrolledUp(true);
        setAutoScrollPaused(true);
      } else {
        setIsUserScrolledUp(false);
        setAutoScrollPaused(false);
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Update custom scrollbar thumb on touch scroll (touch devices / live server)
  useEffect(() => {
    const container = chatScrollRef.current;
    if (!container) return;
    const onTouch = () => updateScrollThumb();
    container.addEventListener("touchmove", onTouch, { passive: true });
    container.addEventListener("touchend", onTouch, { passive: true });
    return () => {
      container.removeEventListener("touchmove", onTouch);
      container.removeEventListener("touchend", onTouch);
    };
  }, [updateScrollThumb]);

  // Keep thumb in sync when content size changes (new messages, resize)
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollThumb);
    ro.observe(el);
    const mo = new MutationObserver(updateScrollThumb);
    mo.observe(el, { childList: true, subtree: true });
    updateScrollThumb();
    return () => { ro.disconnect(); mo.disconnect(); };
  }, [updateScrollThumb]);

  // Wheel on scrollbar track forwards to chat panel
  useEffect(() => {
    const track = scrollTrackRef.current;
    if (!track) return;
    const onWheel = (e) => {
      e.preventDefault();
      if (chatScrollRef.current) chatScrollRef.current.scrollTop += e.deltaY;
    };
    track.addEventListener("wheel", onWheel, { passive: false });
    return () => track.removeEventListener("wheel", onWheel);
  });

  useEffect(() => {
    if (messages.length > 0 && !autoScrollPaused && chatScrollRef.current) {
      const scrollContainer = chatScrollRef.current;
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      });
    }
    const t1 = setTimeout(() => updateScrollThumb(), 50);
    const t2 = setTimeout(() => updateScrollThumb(), 200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [messages, isLoading, complete, autoScrollPaused]);

  const handleExport = () => {
    const hasContent = messages.some((m) => (m.content && m.content.trim()) || m.media || m.filePreview);
    if (!hasContent) return;
    setShowExportPicker(true);
  };

  const doExport = async (format) => {
    setShowExportPicker(false);

    if (format === "text") {
      const allText = messages
        .map((m) => {
          const raw = (m.content || "")
            .replace(/<\/?mark[^>]*>/gi, "")
            .replace(/<[^>]+>/g, "")
            .replace(/^#{1,6}\s+/gm, "")
            .replace(/\*\*(.+?)\*\*/g, "$1")
            .replace(/\*(.+?)\*/g, "$1")
            .replace(/~~(.+?)~~/g, "$1")
            .replace(/`{1,3}[^`]*`{1,3}/g, (match) => match.replace(/`/g, ""))
            .trim();
          const fileNote = (m.media || m.filePreview)
            ? `[Attached Image: ${m.fileName || "image"}]`
            : m.fileName
              ? `[Attached File: ${m.fileName}]`
              : "";
          const combined = [fileNote, raw].filter(Boolean).join("\n");
          return (m.type === "user" ? "User: " : "Linko: ") + combined;
        })
        .filter((line) => line && !line.endsWith(": "))
        .join("\n\n");

      if (!allText) return;

      const blob = new Blob([allText], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CHAT_${getTimestamp()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === "word") {
      const processedMessages = await Promise.all(
        messages.map(async (m) => {
          let imgSrc = null;
          if ((m.media || m.filePreview) && isImageAttachment(m.media || m.filePreview, m.fileName)) {
            imgSrc = await getBase64Image(m.media || m.filePreview);
          }
          return { ...m, resolvedImgSrc: imgSrc };
        })
      );

      const msgHtmlParts = processedMessages
        .map((m) => {
          if (!m.content && !m.resolvedImgSrc && !m.fileName) return "";
          const label = m.type === "user" ? "User" : "Linko";
          const labelColor = m.type === "user" ? "rgb(61,43,144)" : "rgb(30,158,140)";
          const imageHtml = m.resolvedImgSrc
            ? `<div style="margin-bottom:10pt;"><img src="${m.resolvedImgSrc}" width="180" style="width:180pt;max-width:240pt;height:auto;border-radius:6pt;display:block;" /></div>`
            : m.fileName
              ? `<div style="font-size:10pt;color:#6b7280;margin-bottom:4pt;">[Attached: ${m.fileName}]</div>`
              : "";
          const contentHtml = m.content
            ? `<div style="font-size:11pt;line-height:1.6;margin-bottom:10pt;">${markdownToHtml(m.content)}</div>`
            : "";
          return `<div style="margin-bottom:14pt;">
          <div style="font-weight:bold;font-size:12pt;color:${labelColor};margin-bottom:4pt;">${label}:</div>
          ${imageHtml}
          ${contentHtml}
        </div>`;
        })
        .filter(Boolean)
        .join("");

      generateWordFromHtml([msgHtmlParts], `CHAT_${getTimestamp()}.docx`);
    } else {
      const processedMessages = await Promise.all(
        messages.map(async (m) => {
          let imgSrc = null;
          if ((m.media || m.filePreview) && isImageAttachment(m.media || m.filePreview, m.fileName)) {
            imgSrc = await getBase64Image(m.media || m.filePreview);
          }
          return { ...m, resolvedImgSrc: imgSrc };
        })
      );

      const msgHtmlParts = processedMessages
        .map((m) => {
          if (!m.content && !m.resolvedImgSrc && !m.fileName) return "";
          const label = m.type === "user" ? "User" : "Linko";
          const labelColor = m.type === "user" ? "rgb(61,43,144)" : "rgb(30,158,140)";
          const imageHtml = m.resolvedImgSrc
            ? `<div style="margin-bottom:8px;" data-pdf-keep-together="1"><img src="${m.resolvedImgSrc}" style="max-width:300px;max-height:220px;border-radius:6px;object-fit:contain;display:block;" /></div>`
            : m.fileName
              ? `<div style="font-size:10px;color:#6b7280;margin-bottom:4px;">[Attached: ${m.fileName}]</div>`
              : "";
          const contentHtml = m.content
            ? `<div style="font-size:11px;line-height:1.5;">${MD_PDF_STYLE}${markdownToHtml(m.content)}</div>`
            : "";
          return `<div style="margin-bottom:12px;">
          <div style="font-weight:700;font-size:11px;color:${labelColor};margin-bottom:2px;">${label}:</div>
          ${imageHtml}
          ${contentHtml}
        </div>`;
        })
        .filter(Boolean)
        .join("");
      const htmlParts = [msgHtmlParts];

      generatePdfFromHtml(htmlParts, `CHAT_${getTimestamp()}.pdf`);
    }
  };

  return (
    <>
      <div className="flex flex-col flex-1 min-h-0 w-full items-center overflow-hidden">
        <div className="flex flex-col flex-1 min-h-0 w-full px-2 sm:px-4">

          {/* Single row: New Chat | Tabs | AI Rule icon */}
          <div className="action-bar-container flex flex-wrap items-center justify-center sm:justify-between gap-2 sm:gap-3 mb-4 w-full">
            {/* Left: New Chat button */}
            <div className="flex items-center gap-2 sm:flex-1 sm:justify-start">
              <button
                onClick={() => setShowNewChatConfirm(true)}
                disabled={messages.length === 0}
                className={`btn-gradient-border flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 font-bold text-dark-navy text-[10px] sm:text-xs uppercase tracking-wide whitespace-nowrap${messages.length === 0 ? " opacity-40 cursor-not-allowed" : ""}`}
              >
                <img src={AddIcon} alt="new" className="h-3.5 w-3.5 sm:h-4 sm:w-4 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }} />
                {t.newChat}
              </button>
            </div>

            {/* Center: Tab bar */}
            <div className="order-3 xl:order-none basis-full xl:basis-auto w-full xl:w-auto flex items-center justify-center">
              <TranslateTabBar
                activeTab="ai-chat"
                onTabChange={(tab) => {
                  if (tab === "translate") navigate("/translate", { state: { tab: "translate" } });
                  else if (tab === "transcribe") navigate("/translate", { state: { tab: "transcribe" } });
                }}
                inline
              />
            </div>

            {/* Right: AI Rule icon */}
            <div className="flex items-center gap-2 sm:flex-1 sm:justify-end">
              <div
                className="relative"
                onMouseEnter={() => !hasAiAccess && setShowAiTooltip(true)}
                onMouseLeave={() => setShowAiTooltip(false)}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (hasAiAccess) {
                      setAiMenuOpen(true);
                    } else if (isContactRequestSent()) {
                      setShowAlreadySentPopup(true);
                    } else {
                      setShowContactUs(true);
                    }
                  }}
                  className="w-[31px] h-[31px] lg:w-[40px] lg:h-[40px] rounded-full bg-deep-purple flex items-center justify-center cursor-pointer"
                  aria-label="Open AI menu"
                >
                  <img src={AIIcon} alt="AI" className="w-[18px] lg:w-[24px] h-[14px] lg:h-[19px] object-contain" />
                </button>
                {showAiTooltip && !hasAiAccess && (
                  <span className="absolute right-0 top-full mt-2 z-50 px-2.5 py-1.5 rounded bg-white text-gray-900 text-[10px] leading-snug pointer-events-none shadow-md border border-gray-200 max-w-[280px]">
                    {t.aiRulesOrgsOnly}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Token limit warning */}
          {isLimitReached && (
            <div
              className={`w-full flex flex-wrap items-center justify-center gap-2 sm:gap-3.5 px-4 py-2 mb-3 rounded-[12px] shadow-xs flex-shrink-0 ${isTokenExhausted
                ? "bg-[#FDF2F2] border border-[#F8B4B4]"
                : "bg-[#FFF8EB] border border-[#F2C976]/70"
                }`}
            >
              <div className="flex items-center gap-2">
                <AlertCircle
                  className={`h-4 w-4 flex-shrink-0 ${isTokenExhausted ? "text-[#E02424]" : "text-[#C07B00]"
                    }`}
                />
                <span
                  className={`text-[12px] sm:text-[13px] font-roboto font-semibold leading-none ${isTokenExhausted ? "text-[#9B1C1C]" : "text-[#875800]"
                    }`}
                >
                  {isTokenExhausted
                    ? (t.tokenLimitReachedMsg || "You have reached your token limit.")
                    : (t.tokenLimitWarning90Msg || "You have almost reached your token limit.")}
                </span>
              </div>
              <button
                type="button"
                onClick={() => navigate("/plans", { state: { tab: "plans" } })}
                className="flex items-center justify-center h-[28px] px-3.5 rounded-full bg-[#3D2B90] hover:bg-[#2c1e6b] text-white font-roboto font-bold text-[11px] uppercase tracking-wide leading-none transition-all shadow-xs cursor-pointer flex-shrink-0 active:scale-95"
              >
                {t.upgradePlanBtn || "Upgrade Plan"}
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center flex-1">
              <img src={Loader} alt="Loading..." style={{ mixBlendMode: " multiply " }} />
            </div>
          ) : (
            <>
              {/* Main white card — fills remaining vertical space */}
              <div className="bg-white rounded-[27px] p-4 flex flex-col w-full flex-1 min-h-0">

                {/* Content column */}
                <div className="flex flex-1 overflow-hidden min-h-0 w-full">

                  {/* Left column: chat panel + input row stacked */}
                  <div className="flex flex-col flex-1 overflow-hidden min-w-0 gap-3">

                    {/* Chat messages panel + scrollbar side by side */}
                    <div className="flex gap-[10.18px] flex-1 min-h-0">

                      {/* Chat messages panel */}
                      <div
                        ref={chatScrollRef}
                        className="bg-white border border-[#E8E4F8] rounded-[27px] p-3 flex flex-col flex-1 overflow-y-auto no-scrollbar relative min-h-0"
                        onScroll={updateScrollThumb}
                      >
                        {initialRender && messages.length === 0 ? (
                          /* Initial empty state */
                          <div className="flex flex-col items-center justify-center h-full min-h-0 gap-4 px-6">
                            <div className="w-16 h-16 rounded-full bg-deep-purple/20 flex items-center justify-center">
                              <img src={Logo} alt="Linko" className="w-10 h-10 object-contain rounded-full" />
                            </div>
                            <h3 className="font-roboto font-bold text-[18px] sm:text-[20px] text-[#3D2B90] text-center">
                              {t.greeting}
                            </h3>
                            <p className="font-roboto text-[13px] sm:text-[14px] text-[#7376aa] text-center max-w-[320px] leading-relaxed">
                              {t.greetingSubtitle}
                            </p>
                          </div>
                        ) : (
                          /* Messages list — edge to edge */
                          <div className="flex flex-col gap-4 w-full py-2 pb-6">
                            {messages?.map((res, index) => {
                              const isBotEmpty = res?.type === "bot" && !res?.content;
                              return (
                                <div
                                  key={index}
                                  className={`relative w-full ${res?.type === "bot"
                                    ? "flex justify-start items-start gap-2.5"
                                    : "flex justify-end"
                                    }`}
                                >
                                  {res?.type === "bot" && (
                                    <div className="h-7 w-7 flex items-center justify-center overflow-hidden rounded-full flex-shrink-0 bg-white border border-[#E8E4F8] mt-1 shadow-sm">
                                      <img src={Logo} alt="Linko" className="h-full w-full object-cover" />
                                    </div>
                                  )}

                                  {res?.type === "user" && isImageAttachment(res?.media || res?.filePreview, res?.fileName) ? (
                                    <div className="flex flex-col items-end gap-1.5 max-w-[80%]">
                                      {/* User attached image / media — no background wrapper */}
                                      <div
                                        className="max-w-[320px] rounded-2xl overflow-hidden border border-[#C7BFE4] shadow-sm cursor-pointer group relative"
                                        onClick={() => setPreviewImage(res.media || res.filePreview)}
                                        title="Click to view full image"
                                      >
                                        <img
                                          src={res.media || res.filePreview}
                                          alt={res.fileName || "Uploaded attachment"}
                                          className="w-full h-auto max-h-[260px] object-contain block transition-transform duration-200 group-hover:scale-[1.02]"
                                        />
                                        <div className="absolute inset-0 bg-black/15 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                                          <span className="bg-black/60 text-white text-[10px] font-medium px-2 py-1 rounded-md backdrop-blur-xs">
                                            Click to enlarge
                                          </span>
                                        </div>
                                      </div>

                                      {/* If text is attached with the image, display it in its own bubble below */}
                                      {res?.content && res?.content.trim() ? (
                                        <div
                                          className="rounded-[20px] relative bg-[#F3F1F9] text-[#3D2B90] px-4 py-2.5 max-w-full"
                                          style={{
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                          }}
                                        >
                                          <div
                                            className="text-[#3D2B90] [&_p]:block [&_p]:mb-3 [&_p:last-child]:mb-0"
                                            style={{
                                              wordBreak: "break-word",
                                              overflowWrap: "break-word",
                                              boxSizing: "border-box",
                                            }}
                                          >
                                            {renderContent(res?.content)}
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <div
                                      className={`rounded-[20px] relative bg-[#F3F1F9] text-[#3D2B90] px-4 max-w-[80%] ${res?.type === "user" ? "py-2.5" : "pt-3 pb-2.5"}`}
                                      style={{
                                        wordBreak: "break-word",
                                        overflowWrap: "break-word",
                                      }}
                                    >
                                      {/* User attached image / media */}
                                      {isImageAttachment(res?.media || res?.filePreview, res?.fileName) && (
                                        <div
                                          className="mb-2 max-w-[320px] rounded-xl overflow-hidden border border-[#C7BFE4] bg-white shadow-sm cursor-pointer group relative"
                                          onClick={() => setPreviewImage(res.media || res.filePreview)}
                                          title="Click to view full image"
                                        >
                                          <img
                                            src={res.media || res.filePreview}
                                            alt={res.fileName || "Uploaded attachment"}
                                            className="w-full h-auto max-h-[260px] object-contain block transition-transform duration-200 group-hover:scale-[1.02]"
                                          />
                                          <div className="absolute inset-0 bg-black/15 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                                            <span className="bg-black/60 text-white text-[10px] font-medium px-2 py-1 rounded-md backdrop-blur-xs">Click to enlarge</span>
                                          </div>
                                        </div>
                                      )}

                                      {/* User attached document (.docx, .pdf, .txt, etc.) */}
                                      {!isImageAttachment(res?.media || res?.filePreview, res?.fileName) && (res?.media || res?.filePreview || res?.fileName) && (
                                        <div
                                          onClick={() => handleOpenFile(res?.media || res?.filePreview, res?.fileName, res?.fileBlob)}
                                          className="mb-2 inline-flex items-center gap-2.5 px-3.5 py-2.5 bg-white hover:bg-[#EDE9F8]/40 rounded-xl border border-[#C7BFE4] text-xs font-semibold text-[#3D2B90] shadow-sm cursor-pointer transition-all hover:shadow-md hover:border-[#904DB0] group max-w-full select-none"
                                          title="Click to open or download document"
                                        >
                                          <div className="w-7 h-7 rounded-lg bg-[#EDE9F8] flex items-center justify-center flex-shrink-0 group-hover:bg-[#904DB0]/20 transition-colors">
                                            <img src={UploadFileIcon} alt="file" className="w-4 h-4 object-contain" />
                                          </div>
                                          <div className="flex flex-col min-w-0 pr-1 text-left">
                                            <span className="truncate max-w-[200px] sm:max-w-[280px] font-bold text-[#3D2B90] text-[12.5px] leading-tight">
                                              {res?.fileName || (res?.media ? res.media.split("/").pop() : "Document")}
                                            </span>
                                            <span className="text-[10px] text-[#7376aa] font-normal leading-tight mt-0.5 flex items-center gap-1">
                                              <span className="underline group-hover:text-[#3D2B90]">Click to open</span>
                                              <span>•</span>
                                              <span className="uppercase font-semibold">{(res?.fileName || res?.media || "file").split(".").pop()}</span>
                                            </span>
                                          </div>
                                        </div>
                                      )}

                                      {isBotEmpty && isLoading && index === messages.length - 1 ? (
                                        <div className="flex items-center min-h-[28px] py-1">
                                          <div className="bouncing_loader"></div>
                                        </div>
                                      ) : (
                                        res?.content ? (
                                          <div
                                            className="text-[#3D2B90] [&_p]:block [&_p]:mb-3 [&_p:last-child]:mb-0"
                                            style={{
                                              wordBreak: "break-word",
                                              overflowWrap: "break-word",
                                              boxSizing: "border-box",
                                            }}
                                          >
                                            {renderContent(res?.content)}
                                          </div>
                                        ) : null
                                      )}

                                      {res?.type === "bot" && res?.content && (
                                        <div className="flex items-center justify-between mt-1.5 gap-2">
                                          {(() => {
                                            const piVal = res?.personalIdentifiersProtected ?? (index === messages.length - 1 ? personalIdentifiersProtected : null);
                                            if (piVal !== null && piVal !== undefined && !isNaN(Number(piVal))) {
                                              return (
                                                <span className="text-[10px] sm:text-[11px] font-bold text-[#3D2B90] uppercase tracking-wide flex items-center gap-1.5 bg-[#EDE9F8] px-2.5 py-0.5 rounded-full shadow-xs">
                                                  <Shield className="w-3.5 h-3.5 text-[#3D2B90] flex-shrink-0" />
                                                  <span>{t.personalIdentifiersProtected || "Protected Identifiers"}:</span>
                                                  <span className="font-black text-[#3D2B90]">{Number(piVal)}</span>
                                                </span>
                                              );
                                            }
                                            return <div />;
                                          })()}
                                          <button
                                            type="button"
                                            className="flex items-center justify-center w-5 h-5 bg-transparent text-[#3D2B90] hover:opacity-75 transition-opacity cursor-pointer ml-auto"
                                            onClick={() => handleCopyClick(res?.content, index)}
                                            title={index === copiedIndex ? t.copied : t.copy}
                                          >
                                            {index === copiedIndex ? (
                                              <BadgeCheckIcon className="h-4 w-4 text-[#3D2B90]" />
                                            ) : (
                                              <img
                                                src={CopyIcon}
                                                alt="copy"
                                                className="h-4 w-4 object-contain"
                                                style={{ filter: "brightness(0) saturate(100%) invert(18%) sepia(60%) saturate(2200%) hue-rotate(240deg) brightness(85%)" }}
                                              />
                                            )}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <div ref={messageEndRef} />
                          </div>
                        )}
                      </div>{/* end #F3F1F9 panel / chatScrollRef */}

                      {/* Custom scrollbar track */}
                      <div
                        ref={scrollTrackRef}
                        className="relative w-[14px] lg:w-[17.94px] h-full bg-[#C7BFE4] rounded-[17px] flex-shrink-0 cursor-pointer"
                        style={{ visibility: "hidden" }}
                        onPointerDown={(e) => {
                          if (scrollThumbRef.current && scrollThumbRef.current.contains(e.target)) return;
                          e.preventDefault();
                          const el = chatScrollRef.current;
                          const track = scrollTrackRef.current;
                          const thumb = scrollThumbRef.current;
                          if (!el || !track) return;
                          const trackH = track.clientHeight;
                          const rect = track.getBoundingClientRect();
                          const clickY = e.clientY - rect.top;
                          const h = thumbHRef.current;
                          const maxThumbTop = trackH - h;
                          const newTop = Math.min(Math.max(0, clickY - h / 2), maxThumbTop);
                          el.scrollTop = maxThumbTop > 0 ? (newTop / maxThumbTop) * (el.scrollHeight - el.clientHeight) : 0;
                          if (thumb) thumb.style.top = newTop + "px";
                          const startY = e.clientY;
                          const startTop = newTop;
                          const onMove = (ev) => {
                            const delta = ev.clientY - startY;
                            const nt = Math.min(Math.max(0, startTop + delta), maxThumbTop);
                            el.scrollTop = maxThumbTop > 0 ? (nt / maxThumbTop) * (el.scrollHeight - el.clientHeight) : 0;
                            if (thumb) thumb.style.top = nt + "px";
                          };
                          const onUp = () => {
                            window.removeEventListener("pointermove", onMove);
                            window.removeEventListener("pointerup", onUp);
                          };
                          window.addEventListener("pointermove", onMove);
                          window.addEventListener("pointerup", onUp);
                        }}
                      >
                        <div
                          ref={scrollThumbRef}
                          className="absolute w-full bg-[#3D2B90] rounded-[10px] cursor-grab active:cursor-grabbing"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const el = chatScrollRef.current;
                            const track = scrollTrackRef.current;
                            const thumb = scrollThumbRef.current;
                            if (!el || !track || !thumb) return;
                            const trackH = track.clientHeight;
                            const startY = e.clientY;
                            const startTop = thumbTopRef.current;
                            const h = thumbHRef.current;
                            const maxThumbTop = trackH - h;
                            const onMove = (ev) => {
                              const delta = ev.clientY - startY;
                              const nt = Math.min(Math.max(0, startTop + delta), maxThumbTop);
                              thumb.style.top = nt + "px";
                              el.scrollTop = maxThumbTop > 0 ? (nt / maxThumbTop) * (el.scrollHeight - el.clientHeight) : 0;
                            };
                            const onUp = () => {
                              window.removeEventListener("pointermove", onMove);
                              window.removeEventListener("pointerup", onUp);
                            };
                            window.addEventListener("pointermove", onMove);
                            window.addEventListener("pointerup", onUp);
                          }}
                        />
                      </div>

                    </div>{/* end flex row: panel + scrollbar */}

                    {/* Input toolbar inside left column */}
                    {/* Bottom toolbar: suggestion tag / Input / Send */}
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <div className="flex items-center gap-3">

                        {/* Selected suggestion tag */}
                        {selectedOptions && (
                          <div className="flex gap-2 flex-wrap px-1 w-full">
                            <div className="w-auto h-9 pl-3 pr-2 border border-none bg-violet-50 rounded-2xl flex items-center gap-2 text-sm text-black">
                              <span>{selectedOptions}</span>
                              <button
                                onClick={handleRemoveOption}
                                className="text-primary text-2xl cursor-pointer pb-1"
                              >
                                &times;
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Input row */}
                        <form
                          onSubmit={formik.handleSubmit}
                          className="flex items-center gap-3 flex-1 min-w-0"
                        >
                          {/* Hidden file input for chat attachments */}
                          <input
                            ref={chatFileInputRef}
                            type="file"
                            hidden
                            accept="image/*,.pdf,.doc,.docx,.txt,.ppt,.pptx"
                            onChange={handleChatFileSelect}
                            disabled={isLoading || complete || availableTokens === 0}
                          />

                          <div className="relative flex flex-col flex-1 min-w-0 w-[1000px] bg-white border border-[#E8E4F8] rounded-[22px] px-3 lg:px-4 py-2 min-h-[54px] justify-center">
                            {/* Attached file preview chip inside input field */}
                            {chatFile && (
                              <div className="flex items-center gap-2 mb-1.5 p-1.5 bg-[#F3F1F9] border border-[#C7BFE4] rounded-xl w-fit max-w-full">
                                {chatFilePreview ? (
                                  <img src={chatFilePreview} alt="preview" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                                ) : (
                                  <img src={UploadFileIcon} alt="file" className="w-5 h-5 object-contain flex-shrink-0 ml-1" />
                                )}
                                <span className="text-xs text-[#3D2B90] font-medium truncate max-w-[180px] sm:max-w-[300px]">
                                  {chatFile.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={handleRemoveChatFile}
                                  disabled={isLoading || complete}
                                  className="text-gray-400 hover:text-red-500 transition-colors p-0.5 ml-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Remove"
                                >
                                  <CircleX size={16} />
                                </button>
                              </div>
                            )}

                            <div className="relative flex items-center w-full gap-2 lg:gap-3">
                              {/* Attachment Button */}
                              <button
                                type="button"
                                disabled={isLoading || complete || availableTokens === 0}
                                onClick={() => chatFileInputRef.current?.click()}
                                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${chatFile ? "bg-[#3D2B90]/10 text-[#3D2B90]" : "text-[#7376aa] hover:bg-[#F3F1F9] hover:text-[#3D2B90]"
                                  } ${availableTokens === 0 || isLoading || complete ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                title="Upload Image or Document"
                              >
                                <Paperclip size={18} />
                              </button>

                              {/* INPUT label placeholder */}
                              {!formik.values.input && !chatFile && (
                                <span className="pointer-events-none absolute inset-0 flex items-center justify-center select-none pl-8">
                                  <p className="text-[#3D2B90]/40 text-sm lg:text-lg font-medium leading-snug text-center">{t.inputLabel}</p>
                                </span>
                              )}

                              <textarea
                                ref={textareaRef}
                                name="input"
                                rows={1}
                                autoFocus
                                disabled={availableTokens === 0 || isLoading || complete}
                                className={`bg-transparent focus:outline-none border-0 w-full text-[#3D2B90] text-sm lg:text-base resize-none self-center z-10 max-h-[120px] overflow-y-auto ${availableTokens === 0 || isLoading || complete ? "cursor-not-allowed" : ""
                                  }`}
                                style={{
                                  scrollbarWidth: 'thin',
                                  scrollbarColor: '#C7BFE4 transparent'
                                }}
                                value={formik.values.input}
                                onChange={formik.handleChange}
                                onBlur={(e) => {
                                  const trimmedValue = e.target.value.replace(/^\s+|\s+$/g, "");
                                  setTimeout(() => formik.setFieldValue("input", trimmedValue), 0);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    if (!isLoading && !complete && (formik.values.input.trim() !== "" || chatFile)) {
                                      formik.handleSubmit();
                                    }
                                  }
                                }}
                              />

                              {/* Pause / Send button */}
                              {(isLoading || complete) ? (
                                <button
                                  type="button"
                                  disabled={true}
                                  className="flex-shrink-0 self-center w-[36px] h-[36px] lg:w-[42px] lg:h-[42px] rounded-full flex items-center justify-center bg-[#C7BFE4] cursor-not-allowed opacity-60"
                                  title="Please wait for response"
                                >
                                  <img
                                    src={PauseIcon}
                                    alt="Disabled"
                                    className="w-4 h-4 lg:w-5 lg:h-5 object-contain brightness-0 invert"
                                  />
                                </button>
                              ) : (formik.values.input.trim() || chatFile) ? (
                                <button
                                  type="button"
                                  className="cursor-pointer flex-shrink-0 self-center w-[36px] h-[36px] lg:w-[42px] lg:h-[42px] rounded-full bg-deep-purple flex items-center justify-center hover:bg-[#2e1f72] transition-colors"
                                  onClick={() => {
                                    formik.handleSubmit();
                                  }}
                                >
                                  <img src={PauseIcon} alt="Send" className="w-4 h-4 lg:w-5 lg:h-5 object-contain brightness-0 invert" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={true}
                                  className="flex-shrink-0 self-center w-[36px] h-[36px] lg:w-[42px] lg:h-[42px] rounded-full flex items-center justify-center bg-[#C7BFE4] cursor-not-allowed opacity-60"
                                >
                                  <img
                                    src={PauseIcon}
                                    alt="Send"
                                    className="w-4 h-4 lg:w-5 lg:h-5 object-contain brightness-0 invert"
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                        </form>
                        {/* Spacer matching scrollbar width to align input with panel above */}
                        <div className="w-[14px] lg:w-[17.94px] flex-shrink-0" />
                      </div>
                    </div>{/* end flex-col token+toolbar wrapper */}

                  </div>{/* end left column */}

                </div>{/* end outer flex row */}
              </div>

              {/* Bottom bar: warning left + Export right */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 flex-shrink-0 mt-2 sm:mt-4">
                <div className={`flex items-center gap-2 flex-shrink-0 min-w-0 ${availableTokens === 0 ? "bg-red-50 border border-red-100 px-2 py-1 rounded-md" : ""}`}>
                  {availableTokens === 0 ? (
                    <AlertCircle className="h-[18px] w-[18px] text-red-500 flex-shrink-0" />
                  ) : (
                    <img src={WarningIcon} alt="warning" className="h-[18px] w-[18px] object-contain flex-shrink-0" />
                  )}
                  {availableTokens === 0 ? (
                    <span
                      className="font-roboto font-bold text-[13px] text-red-500 leading-none tracking-normal truncate cursor-pointer hover:underline"
                      onClick={() => navigate("/plans")}
                    >
                      {t.upgradePlan}
                    </span>
                  ) : privacyStatus === "protecting" ? (
                    <span className="font-roboto font-bold text-[13px] text-[#3D2B90] leading-tight tracking-normal">
                      {t.protectingContent || "Protecting your content…"}
                      <br />
                      {t.checkingPersonalInfo || "Checking for personal information."}
                    </span>
                  ) : privacyStatus === "completed" ? (
                    <span className="font-roboto font-bold text-[13px] text-[#3D2B90] leading-tight tracking-normal">
                      {(t.personalIdentifiersProtectedLabel || "Personal identifiers protected") + ": " + (typeof personalIdentifiersProtected === "number" ? personalIdentifiersProtected : 0)}
                    </span>
                  ) : privacyStatus === "reminder" ? (
                    <span className="font-roboto font-bold text-[13px] text-[#3D2B90] leading-tight tracking-normal">
                      {t.chatExportReminder && t.chatExportReminder.includes("—") ? (
                        <>
                          {t.chatExportReminder.split("—")[0].trim()}
                          <br />
                          {t.chatExportReminder.split("—")[1].trim()}
                        </>
                      ) : t.chatExportReminder && t.chatExportReminder.includes("-") ? (
                        <>
                          {t.chatExportReminder.split("-")[0].trim()}
                          <br />
                          {t.chatExportReminder.split("-")[1].trim()}
                        </>
                      ) : (
                        <>
                          Remember to export
                          <br />
                          this conversation is not stored or used for AI training.
                        </>
                      )}
                    </span>
                  ) : (
                    <span className="font-roboto font-bold text-[13px] text-[#3D2B90] leading-tight tracking-normal">
                      {t.chatExportReminder && t.chatExportReminder.includes("—") ? (
                        <>
                          {t.chatExportReminder.split("—")[0].trim()}
                          <br />
                          {t.chatExportReminder.split("—")[1].trim()}
                        </>
                      ) : t.chatExportReminder && t.chatExportReminder.includes("-") ? (
                        <>
                          {t.chatExportReminder.split("-")[0].trim()}
                          <br />
                          {t.chatExportReminder.split("-")[1].trim()}
                        </>
                      ) : (
                        <>
                          Remember to export
                          <br />
                          this conversation is not stored or used for AI training.
                        </>
                      )}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleExport}
                  disabled={messages.length === 0}
                  className={`btn-gradient-border w-full sm:w-auto flex items-center justify-center gap-2 h-[38px] px-6 font-roboto font-bold text-dark-navy text-xs uppercase tracking-wide whitespace-nowrap flex-shrink-0 transition-all ${messages.length > 0
                    ? "cursor-pointer hover:opacity-85"
                    : "opacity-40 cursor-not-allowed"
                    }`}
                >
                  <img
                    src={ExportIcon}
                    alt="export"
                    className="h-3.5 w-3.5 object-contain flex-shrink-0"
                    style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }}
                  />
                  {t.exportBtn}
                </button>
              </div>

            </>
          )}

        </div>
      </div>

      {/* Export Format Picker */}
      {showExportPicker && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setShowExportPicker(false)}
          onKeyDown={(e) => { if (e.key === "Escape") setShowExportPicker(false); }}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <div className="relative bg-white rounded-[27px] p-6 w-full max-w-sm flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowExportPicker(false)}
              className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center text-[#3D2B90] hover:opacity-70 transition-opacity"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L15 15M15 1L1 15" stroke="#3D2B90" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">{t.downloadAs}</h2>
            <div className="flex gap-2.5 sm:gap-3">
              <button
                type="button"
                onClick={() => doExport("text")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.text}
              </button>
              <button
                type="button"
                onClick={() => doExport("pdf")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.pdf}
              </button>
              <button
                type="button"
                onClick={() => doExport("word")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.word || "WORD"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Chat Confirmation Modal */}
      {showNewChatConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4" onClick={() => setShowNewChatConfirm(false)}>
          <div className="bg-white rounded-[27px] p-6 w-full max-w-md flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">
              {t.newChatSessionTitle}
            </h2>
            <p className="text-[12px] text-[#9190A4] text-center font-roboto">
              {t.newChatConfirm}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowNewChatConfirm(false)}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.newSessionCancel}
              </button>
              <button
                type="button"
                onClick={() => { setShowNewChatConfirm(false); handleNewChatClick(); }}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#2e1f72] transition-colors"
              >
                {t.newSessionConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

      <MenuAIPanel
        isOpen={aiMenuOpen}
        onClose={() => setAiMenuOpen(false)}
        presets={presets}
        originalText={messages.filter(m => m.type === "bot").slice(-1)[0]?.content || ""}
        translatedText={messages.filter(m => m.type === "user").slice(-1)[0]?.content || ""}
        mode="chat"
        hasChatHistory={Array.isArray(messages) && messages.length > 0}
        uploadedFile={chatFile || uploadFile}
        onResult={(improved) => {
          if (!improved) return;

          const itemsArray = Array.isArray(improved)
            ? improved
            : Array.isArray(improved?.data)
            ? improved.data
            : typeof improved === "object"
            ? Object.keys(improved)
                .filter((k) => !isNaN(Number(k)))
                .sort((a, b) => Number(a) - Number(b))
                .map((k) => improved[k])
                .filter((v) => v && typeof v === "object" && (v.question || v.answer))
            : null;

          if (itemsArray && itemsArray.length > 0) {
            const newMessages = [];
            itemsArray.forEach((item) => {
              if (item.question) {
                newMessages.push({
                  type: "user",
                  content: item.question,
                  media: item.media || null,
                  fileName: item.fileName || null,
                });
              }
              if (item.answer || item.response || item.text) {
                newMessages.push({
                  type: "bot",
                  content: item.answer || item.response || item.text || "",
                  personalIdentifiersProtected:
                    typeof item.personalIdentifiersProtected === "number"
                      ? item.personalIdentifiersProtected
                      : null,
                  _id: item._id,
                });
              }
            });
            if (newMessages.length > 0) {
              setMessages(newMessages);
              try {
                localStorage.setItem(LS_CHAT_KEY, JSON.stringify(newMessages));
              } catch (_) {}
              return;
            }
          }

          if (typeof improved === "object") {
            const content = improved.answer || improved.response || improved.text || improved.translation || "";
            if (content) {
              setMessages((prev) => [
                ...prev,
                {
                  type: "bot",
                  content,
                  personalIdentifiersProtected:
                    typeof improved.personalIdentifiersProtected === "number"
                      ? improved.personalIdentifiersProtected
                      : null,
                },
              ]);
            }
            return;
          }

          if (typeof improved === "string") {
            setMessages((prev) => [...prev, { type: "bot", content: improved }]);
          }
        }}
      />

      {/* Upload File Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4" onClick={handleCloseUploadModal}>
          <div className="bg-white rounded-[27px] p-6 w-full max-w-md flex flex-col gap-4 relative" onClick={(e) => e.stopPropagation()}>

            {/* Close */}
            <button
              type="button"
              onClick={handleCloseUploadModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <CircleX size={22} />
            </button>

            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">
              {t.uploadFileHeading}
            </h2>

            {/* Language dropdown */}
            <div className="relative" ref={uploadLangRef}>
              <button
                type="button"
                onClick={() => setUploadLangOpen((v) => !v)}
                className="flex items-center justify-between w-full px-4 py-2 border border-[#C7BFE4] rounded-xl text-sm text-[#42445A] bg-[#F3F1F9]"
              >
                <span>
                  {uploadLang || t.selectTargetLanguage}
                  {!uploadLang && <span className="text-red-500"> *</span>}
                </span>
                <img src={DropdownIcon} alt="dropdown" className="h-3 w-3 object-contain" />
              </button>
              {uploadLangOpen && (
                <div className="absolute left-0 mt-1 w-full bg-white rounded-xl shadow-lg z-50 border border-[#C7BFE4]">
                  <div className="p-2">
                    <input
                      type="text"
                      placeholder={t.searchLangPlaceholder}
                      value={uploadLangSearch}
                      onChange={(e) => setUploadLangSearch(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs border border-[#C7BFE4] rounded-lg focus:outline-none"
                    />
                  </div>
                  <div className="max-h-44 overflow-y-auto dropdown-scroll">
                    {filteredUploadLangs.length > 0 ? (
                      filteredUploadLangs.map((item, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => { setUploadLang(item.name); setUploadLangOpen(false); setUploadLangSearch(""); }}
                          className="block w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-[#F3F1F9] border-b border-[#C7BFE4] last:border-0"
                        >
                          {item.name}
                        </button>
                      ))
                    ) : (
                      <p className="px-4 py-2 text-xs text-gray-500 text-center">{t.noLanguageFoundDoc}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* File picker */}
            <div className="relative">
              <input
                ref={uploadFileInputRef}
                type="file"
                hidden
                accept=".ppt,.pptx,.doc,.docx,.txt,.pdf,image/*"
                onChange={handleUploadFileSelect}
                disabled={uploadLoading}
              />
              <label
                onClick={() => uploadFileInputRef.current?.click()}
                className="cursor-pointer flex flex-col items-center justify-center gap-1 border border-dashed border-[#3D2B90] rounded-xl p-5 bg-[#F3F1F9] text-xs text-[#3D2B90] font-semibold text-center min-h-[80px]"
              >
                {uploadFile ? (
                  <span className="break-all">{uploadFile.name}</span>
                ) : (
                  <>
                    <img src={UploadFileIcon} alt="upload" className="h-5 w-5 object-contain mb-1" />
                    {t.selectFile}
                    {!uploadFile && <span className="text-red-500"> *</span>}
                    <span className="font-normal text-[10px] text-gray-400">.PPT .PPTX .DOC .DOCX .TXT .PDF .IMAGE</span>
                  </>
                )}
              </label>
              {uploadFile && (
                <button
                  type="button"
                  disabled={uploadLoading}
                  onClick={() => setUploadFile(null)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                >
                  <CircleX size={18} />
                </button>
              )}
            </div>

            {/* Submit */}
            {availableTokens === 0 && (
              <p className="text-[11px] font-roboto font-semibold text-red-500 text-center"> {t.upgradePlan}</p>
            )}
            <button
              type="button"
              onClick={handleUploadSubmit}
              disabled={uploadLoading || availableTokens === 0}
              className="flex items-center justify-center h-[40px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-6 w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {uploadLoading ? (
                <div className="loader" />
              ) : (
                t.translateAndDownload
              )}
            </button>

          </div>
        </div>
      )}

      {/* Full Image Preview Lightbox Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-xs p-4 sm:p-6"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 sm:-right-10 text-white hover:text-gray-300 transition-colors p-1"
              title="Close"
            >
              <CircleX size={28} />
            </button>
            <img
              src={previewImage}
              alt="Full Preview"
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl border border-white/20 bg-black/40"
            />
          </div>
        </div>
      )}
      {showAlreadySentPopup && (
        <AlreadySentPopup
          t={t}
          onClose={() => setShowAlreadySentPopup(false)}
        />
      )}
      {showContactUs && (
        <ContactUsPopup
          t={t}
          onClose={() => setShowContactUs(false)}
          onSubmit={handleContactUsSubmit}
          isSubmitting={isContactUsSubmitting}
          isSuccess={false}
          prefilledEmail={readStoredEmail() || ""}
        />
      )}
      {contactUsSuccess && (
        <ContactUsPopup
          t={t}
          onClose={() => setContactUsSuccess(false)}
          onSubmit={() => {}}
          isSubmitting={false}
          isSuccess={true}
        />
      )}
    </>
  );
};

export default Chat;

export const TypingResponse = ({
  message,
  typingSpeed = 4,
  setComplete,
  isClick,
  isUserScrolledUp,
  setIsUserScrolledUp,
  chatScrollRef,
}) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  const containerRef = useRef(null);
  let typingTimer = useRef(null);
  let lastTime = useRef(Date.now());

  useEffect(() => {
    if (isClick) {
      setDisplayedText(displayedText);
      setIsTyping(false);
      setComplete(false);
      return;
    }
    if (currentIndex < message.length && isTyping) {
      setComplete(true);
      typingTimer.current = setTimeout(() => {
        setDisplayedText((prev) => prev + message[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, typingSpeed);
    } else {
      setIsTyping(false);
    }
    return () => clearTimeout(typingTimer.current);
  }, [currentIndex, message, typingSpeed, isTyping]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastTime.current = Date.now();
      } else {
        const elapsedTime = Date.now() - lastTime.current;
        const skippedChars = Math.floor(elapsedTime / typingSpeed);
        setCurrentIndex((prev) => Math.min(prev + skippedChars, message.length));
        setDisplayedText(
          message.slice(0, Math.min(currentIndex + skippedChars, message.length))
        );
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [message, typingSpeed, currentIndex]);

  useEffect(() => {
    if (!isUserScrolledUp && isTyping && chatScrollRef?.current) {
      const container = chatScrollRef.current;
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }, [displayedText, isTyping, isUserScrolledUp, chatScrollRef]);

  useEffect(() => {
    if (message.length === currentIndex) {
      setComplete(false);
    }
  }, [displayedText]);

  useEffect(() => {
    return () => clearTimeout(typingTimer.current);
  }, []);

  return (
    <div ref={containerRef} className="max-w-3xl mx-auto overflow-auto rounded-lg [&_p]:block [&_p]:mb-3 [&_p:last-child]:mb-0">
      <div className="bg-none rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="flex-1">
            <div className="prose">{renderContent(displayedText)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};